var kind             = require("enyo/kind"),
    ready            = require("enyo/ready"),
    dispatcher       = require("enyo/dispatcher"),
    CoreObject       = require("enyo/CoreObject"),
    AnimationSupport = require("enyo-anim/AnimationSupport"),
    CanvasRenderer;

// Make sure enyo listens for visibilitychange events
ready(function() {
    dispatcher.listen(document, "visibilitychange");
});

/**
 * [CanvasRenderer]{@link enyo-canvas.CanvasRenderer} is a basic implementation
 * for rendering components of a [Canvas]{@link enyo-canvas.Canvas} control.
 *
 * The renderer takes care of setting up a 2D render context and maps pixels in
 * canvas space to css pixels in css space, regardless of devicePixelRatio and
 * backingStorePixelRatio.
 *
 * This renderer provides a render loop to update state and draw new frames as
 * needed. The strategy used for this is very simple: As soon as any control
 * marks the scene as `dirty`, the entire scene will be redrawn.
 *
 * @class   enyo-canvas.CanvasRenderer
 * @extends enyo.CoreObject
 * @public
 */
CanvasRenderer = kind(/** @lends enyo-canvas.CanvasRenderer.prototype */ {

    /**
     * @private
     */
    name: "enyo-canvas.CanvasRenderer",

    /**
     * @private
     */
    kind: CoreObject,

    /**
     * @private
     */
    mixins: [AnimationSupport],

    /**
     * Whether to clear the frame by drawing a black rectangle, or by using
     * `clearRect`. Note that `clearRect` can be finnicky might not always work,
     * especially in Chrome.
     *
     * Supported values are:
     *  - "fill", for drawing a black rectangle
     *  - "clear", for using `clearRect`.
     *
     * @type    {string}
     * @default "fill"
     * @public
     */
    clearMode: "fill",

    /**
     * Flag whether to enable imageSmoothing.
     *
     * @type    {?string}
     * @default true
     * @public
     */
    imageSmoothingEnabled: true,

    /**
     * Set the imageSmoothingQuality.
     *
     * @type    {string}
     * @default "high"
     * @public
     */
    imageSmoothingQuality: "medium",

    /**
     * Flag whether the canvas should support alpha transparency.
     *
     * @type    {number}
     * @default false
     * @public
     */
    alpha: false,

    /**
     * The Canvas control
     *
     * @type {?enyo-canvas.Canvas}
     * @public
     */
    scene: null,

    /**
     * The on-screen canvas
     *
     * @type {?HTMLCanvasElement}
     * @private
     */
    onScreenCanvas: null,

    /**
     * The context for the on-screen canvas
     *
     * @type {?CanvasRenderingContext2D}
     * @private
     */
    onScreenContext: null,

    /**
     * Flags whether to setup an off-screen render context.
     *
     * @type     {boolean}
     * @default  true
     * @public
     */
    renderOffScreen: true,

    /**
     * The canvas used for rendering
     *
     * @type {?HTMLCanvasElement}
     * @private
     */
    renderCanvas: null,

    /**
     * The context used for rendering
     *
     * @type {?CanvasRenderingContext2D}
     * @private
     */
    renderContext: null,

    /**
     * @private
     */
    handlers: {
        visibilitychange: "visibilityChanged"
    },

    /**
     * Setup renderer
     *
     * @param  {Object}  props  Optional instance properties
     * @method
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.visibilityChanged = this.bindSafely(this.visibilityChanged);
            this.tick              = this.bindSafely(this.tick);
        };
    }),

    /**
     * @method
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.stop();
            sup.apply(this, arguments);
        };
    }),

    // Setup Scene
    // --------------------------------

    /**
     * Setup the render context for a new scene if the scene has a node.
     *
     * @private
     */
    setupScene: function() {
        var sceneCanvas           = this.scene.hasNode(),
            renderOffScreen       = this.renderOffScreen,
            alpha                 = this.alpha,
            imageSmoothingEnabled = this.imageSmoothingEnabled,
            imageSmoothingQuality = this.imageSmoothingQuality,
            renderCanvas,
            renderContext,
            sceneContext;

        if (sceneCanvas) {
            renderCanvas  = renderOffScreen ? document.createElement("canvas") : sceneCanvas;
            renderContext = CanvasRenderer.setupContext(renderCanvas, {
                alpha:                 alpha,
                imageSmoothingEnabled: imageSmoothingEnabled,
                imageSmoothingQuality: imageSmoothingQuality
            });

            sceneContext = renderOffScreen ? CanvasRenderer.setupContext(sceneCanvas, {
                alpha:                 alpha,
                imageSmoothingEnabled: false
            }) : renderContext;

            this.onScreenCanvas  = sceneCanvas;
            this.onScreenContext = sceneContext;
            this.renderCanvas    = renderCanvas;
            this.renderContext   = renderContext;

            // This ensures the renderCanvas has the same bounds as the
            // sceneCanvas
            renderOffScreen && this.scaleScene(sceneCanvas.getBoundingClientRect());
        }
    },

    /**
     * Set the canvas size based on bounds in css pixels.
     *
     * @param  {DOMRect}  bounds  The new bounds of the canvas.
     * @public
     */
    scaleScene: function(bounds) {
        var onScreenCanvas  = this.onScreenCanvas,
            onScreenContext = this.onScreenContext,
            renderCanvas    = this.renderCanvas,
            renderContext   = this.renderContext,
            pixelRatio;

        if (!this.onScreenCanvas
            || !this.onScreenContext
            || !this.renderCanvas
            || !this.renderContext) {
            return;
        }

        pixelRatio            = CanvasRenderer.calcPixelRatio(onScreenContext);
        onScreenCanvas.width  = bounds.width * pixelRatio;
        onScreenCanvas.height = bounds.height * pixelRatio;

        // In case of off-screen rendering
        if (renderCanvas !== onScreenCanvas) {
            pixelRatio          = CanvasRenderer.calcPixelRatio(renderContext);
            renderCanvas.width  = bounds.width * pixelRatio;
            renderCanvas.height = bounds.height * pixelRatio;

            // Scale off-screen canvas to match on-screen canvas
            renderCanvas.style.width  = bounds.width + "px";
            renderCanvas.style.height = bounds.height + "px";
        }
    },

    /**
     * Stop render loop, delete all references to on-screen and render canvas.
     *
     * @public
     */
    teardownScene: function() {
        this.stop();
        this.onScreenCanvas  = null;
        this.onScreenContext = null;
        this.renderCanvas    = null;
        this.renderContext   = null;
    },

    /**
     * Setup a new scene or teardown the old one
     *
     * @param  {boolean}  previousScene  The previous scene
     * @private
     */
    sceneChanged: function(previousScene) {
        var currentScene = this.scene;

        if (previousScene !== currentScene) {
            currentScene ? this.setupScene() : this.teardownScene();
        }
    },

    // Render Loop
    // --------------------------------

    /**
     * Temporarily stop the renderer when the browser tab turns inactive.
     *
     * @private
     */
    visibilityChanged: function() {
        document.hidden ? this.stop() : this.start();
    },

    /**
     * Start the renderer.
     *
     * @public
     */
    start: function() {
        if (!this.running) {
            this.set("running", true);
            this.tick();
        }
    },

    /**
     * Render a frame and schedule the next one.
     *
     * @private
     */
    tick: function() {
        this.scene.update();
        this.draw();
        this.requestAnimationFrame("tick", this.tick);
    },

    /**
     * Applies a transform to the render context. This method compensates for
     * devicePixelRatio and backingStorePixelRatio such that 1 pixel in canvas
     * space equals 1 css pixel on screen.
     *
     * @param {number}  a  Horizontal scaling
     * @param {number}  b  Horizontal skewing
     * @param {number}  c  Vertical skewing
     * @param {number}  d  Vertical scaling
     * @param {number}  e  Horizontal moving
     * @param {number}  f  Vertical moving
     *
     * @public
     */
    setTransform: function(a, b, c, d, e, f) {
        var renderContext = this.renderContext,
            pixelRatio    = CanvasRenderer.calcPixelRatio(renderContext);

        renderContext.setTransform(
            a * pixelRatio,
            b * pixelRatio,
            c * pixelRatio,
            d * pixelRatio,
            e * pixelRatio,
            f * pixelRatio
        );
    },

    /**
     * Renders all children if canvas is dirty.
     *
     * @protected
     */
    draw: function() {
        var scene           = this.scene,
            viewport        = scene.viewport,
            children        = scene.children,
            renderContext   = this.renderContext,
            renderCanvas    = this.renderCanvas,
            onScreenContext = this.onScreenContext,
            onScreenCanvas  = this.onScreenCanvas,
            i,
            n,
            child;

        // Can't draw on canvas with no width or height.
        if (!onScreenCanvas.clientWidth || !onScreenCanvas.clientHeight) {
            return;
        }

        if (scene.dirty) {
            renderContext.globalAlpha              = 1;
            renderContext.globalCompositeOperation = "source-over";

            // Clear render canvas
            switch (this.clearMode) {
                case "clear":
                    renderContext.clearRect(
                        viewport.x,
                        viewport.y,
                        viewport.width,
                        viewport.height
                    );
                    break;
                case "fill":
                default:
                    renderContext.beginPath();
                    renderContext.fillStyle = "rgba(0, 0, 0, 1)";
                    renderContext.fillRect(
                        viewport.x,
                        viewport.y,
                        viewport.width,
                        viewport.height
                    );
                    break;
            }

            // Draw all children
            for (i = 0, n = children.length; i < n; i++) {
                child = children[i];
                child.draw(renderContext);
                child.set("dirty", false);
            }

            // If we rendered off-screen, apply changes to on-screen canvas as
            // needed.
            if (onScreenCanvas !== renderCanvas) {
                onScreenContext.globalAlpha              = 1;
                onScreenContext.globalCompositeOperation = "source-over";

                switch (this.clearMode) {
                    case "clear":
                        onScreenContext.clearRect(0, 0, viewport.width, viewport.height);
                        break;
                    case "fill":
                    default:
                        onScreenContext.beginPath();
                        onScreenContext.fillStyle = "rgba(0, 0, 0, 1)";
                        onScreenContext.fillRect(0, 0, viewport.width, viewport.height);
                        break;
                }
                onScreenContext.drawImage(renderCanvas, 0, 0);
            }

            // Scene is no longer dirty
            scene.set("dirty", false);
        }
    },

    /**
     * Stop the render loop.
     *
     * @public
     */
    stop: function() {
        if (this.running) {
            this.cancelAnimationFrame("tick");
            this.set("running", false);
        }
    },

    // Static Utility methods
    // --------------------------------

    statics: /** @lends enyo-canvas.CanvasRenderer */ {

        /**
         * Setup the canvas for rendering.
         *
         * @param  {HTMLCanvasElement}  canvas  The canvas for which to setup a
         *                                      render context
         * @param  {Object}   options                        The options
         * @param  {boolean}  options.alpha                  Whether the context
         *                                                   should usa an alpha
         *                                                   channel
         * @param  {boolean}  options.imageSmoothingEnabled  Whether image smoothing
         *                                                   should be enabled
         * @param  {string=}  option.imageSmoothingQuality   The image smoothing
         *                                                   quality
         * @return  {CanvasRenderingContext2D}  The rendering context
         * @method
         * @static
         * @public
         */
        setupContext: function(canvas, options) {
            var context,
                pixelRatio;

            context = canvas.getContext("2d", {
                alpha: options.alpha
            });

            context.webkitImageSmoothingEnabled = options.imageSmoothingEnabled;
            context.mozImageSmoothingEnabled    = options.imageSmoothingEnabled;
            context.msImageSmoothingEnabled     = options.imageSmoothingEnabled;
            context.imageSmoothingEnabled       = options.imageSmoothingEnabled;

            if (options.imageSmoothingQuality) {
                context.imageSmoothingQuality = options.imageSmoothingQuality;
            }

            pixelRatio = CanvasRenderer.calcPixelRatio(context);
            context.scale(pixelRatio, pixelRatio);

            return context;
        },

        /**
         * Calculates a scale factor to apply to the context such that 1 pixel
         * in canvas space equals 1 css pixel on screen.
         *
         * @param   {CanvasRenderingContext2D}  context  The render context
         * @return  {number}  The ratio between canvas pixels and css pixels
         * @method
         * @static
         * @public
         */
        calcPixelRatio: function(context) {
            var devicePixelRatio,
                backingStoreRatio;

            devicePixelRatio  = window.devicePixelRatio || 1;
            backingStoreRatio = context.webkitBackingStorePixelRatio
                || context.mozBackingStorePixelRatio
                || context.msBackingStorePixelRatio
                || context.oBackingStorePixelRatio
                || context.backingStorePixelRatio || 1;

            return devicePixelRatio / backingStoreRatio;
        }
    }

});
module.exports = CanvasRenderer;
